import { useState, useEffect, useCallback } from 'react';
import { agentFetch } from '../pages/agent/AgentLayout';

/**
 * useAgentDirectory — annuaire agents (visibilité globale 2026-06-12).
 *
 * Depuis que tous les agents voient tous les leads, les cartes affichent
 * l'agent référent. `assignedAgentId` étant un id opaque, ce hook charge
 * une fois `GET /api/auth/annuaire` ({ agents: [{id, email, active}],
 * me }) et expose `referentLabel(id)` :
 *   - id de l'agent connecté  → « vous » (isMe: true)
 *   - id connu                → partie locale de l'email (avant le @),
 *                               inactive: true si le compte est désactivé
 *   - id inconnu / annuaire indisponible → id raccourci (fallback dégradé)
 *   - null                    → « non assigné »
 */
export function useAgentDirectory() {
  const [dir, setDir] = useState({ byId: null, meId: null });

  useEffect(() => {
    let cancelled = false;
    agentFetch('/api/auth/annuaire')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const byId = {};
        (data.agents || []).forEach((a) => { byId[a.id] = a; });
        setDir({ byId, meId: data.me || null });
      })
      .catch(() => {
        // Annuaire indisponible : referentLabel retombe sur l'id raccourci.
      });
    return () => { cancelled = true; };
  }, []);

  const referentLabel = useCallback((assignedAgentId) => {
    if (!assignedAgentId) {
      return { text: 'non assigné', isMe: false, inactive: false };
    }
    if (dir.meId && assignedAgentId === dir.meId) {
      return { text: 'vous', isMe: true, inactive: false };
    }
    const entry = dir.byId ? dir.byId[assignedAgentId] : null;
    if (!entry) {
      return {
        text: `${assignedAgentId.slice(0, 8)}…`,
        isMe: false,
        inactive: false,
      };
    }
    return {
      text: entry.email.split('@')[0],
      isMe: false,
      inactive: !entry.active,
    };
  }, [dir]);

  return { referentLabel, meId: dir.meId };
}

/**
 * Rendu compact du référent (texte coloré, « vous » mis en avant,
 * « (inactif) » pour un compte désactivé). Utilisé sur les cartes
 * AgentLeads / AgentClients et le détail d'un lead.
 */
export function ReferentTag({ label }) {
  return (
    <span
      style={{
        fontWeight: label.isMe ? 700 : 500,
        color: label.isMe ? '#3D8B5E' : '#8A7250',
      }}
    >
      {label.text}
      {label.inactive ? ' (inactif)' : ''}
    </span>
  );
}
